import {
  buildSpecText,
  normalizeSpecEntries,
  normalizeSpecValues,
  expandSpecCombinations,
  toSpecEntries,
  type SpecAxis,
  type SpecEntry,
  type SpecValues,
} from "@/lib/shop/sku-specs";
import { normalizeMediaPrices, publicMediaPrices, type AiMediaPrice, type AiMediaUsage } from "./media-pricing";

export type AiSpecRole =
  | "quality"
  | "size"
  | "aspect"
  | "duration"
  | "audio"
  | "context"
  | "custom";

export const AI_SPEC_ROLES: { value: AiSpecRole; label: string; hint: string }[] =
  [
    { value: "quality", label: "质量", hint: "low / medium / high" },
    { value: "size", label: "尺寸", hint: "1024x1024 这样的像素写法" },
    { value: "aspect", label: "比例", hint: "1:1 / 16:9" },
    { value: "duration", label: "时长", hint: "秒数档位，如 5 / 10" },
    { value: "audio", label: "有声", hint: "有 / 无" },
    { value: "context", label: "token", hint: "按当次输入 Token 匹配，填写原始整数或不限" },
    { value: "custom", label: "自定义", hint: "只参与计费，不发给上游" },
  ];

const ROLE_VALUES = new Set<string>(AI_SPEC_ROLES.map((item) => item.value));

export function specRoleLabel(role: AiSpecRole): string {
  return AI_SPEC_ROLES.find((item) => item.value === role)?.label ?? role;
}

export type AiSpecAxis = {
  role: AiSpecRole;
  name: string;
  values: string[];
};

export type AiPriceTier = {
  specs: SpecEntry[];
  price: number | null;
  maxInputTokens?: number | null;
};

export type AiPriceUnit = "call" | "image" | "second" | "million_tokens";

export type AiOutputTarget = "text" | "image" | "audio" | "video";
export type AiOutputPrice = {
  target: AiOutputTarget;
  unit: AiPriceUnit;
  axes: AiSpecAxis[];
  tiers: AiPriceTier[];
  default: SpecEntry[];
};
export const AI_OUTPUT_TARGETS = [
  { value: "text", label: "文本输出" }, { value: "image", label: "图片输出" },
  { value: "audio", label: "音频输出" }, { value: "video", label: "视频输出" },
] satisfies { value: AiOutputTarget; label: string }[];
export function outputPriceUnits(target: AiOutputTarget) {
  return AI_PRICE_UNITS.filter(option => option.value === "million_tokens" || option.value === (target === "image" ? "image" : target === "text" ? "million_tokens" : "second"));
}

export const AI_PRICE_UNITS: { value: AiPriceUnit; label: string }[] = [
  { value: "call", label: "每次" },
  { value: "image", label: "每张" },
  { value: "second", label: "每秒" },
  { value: "million_tokens", label: "每 M Token" },
];

export type AiTokenRates = { input: number | null; cacheWrite?: number | null; cached: number | null; output: number | null };
export type AiTokenTier = AiTokenRates & { maxInputTokens: number | null };
export type AiTokenInputPricing = Omit<AiTokenRates, "output"> & { contextTiers?: Omit<AiTokenTier, "output">[] };
export type AiBillingStep = { inputTokens: number; inputTokensReported?: boolean; cachedTokens: number; cacheWriteTokens?: number; outputTokens: number; outputTokensReported?: boolean; outputSeconds?: number; media?: AiMediaUsage };
export type AiBillingUsage = { steps?: AiBillingStep[]; webSearchCalls?: number };

export function validTokenTiers(tiers: readonly AiTokenTier[]): boolean {
  return tiers.length > 0 && tiers.length <= 50 && tiers.every((tier, index) =>
    tier.maxInputTokens === null
      ? index === tiers.length - 1
      : Number.isSafeInteger(tier.maxInputTokens) && tier.maxInputTokens > (index ? tiers[index - 1].maxInputTokens ?? Infinity : 0)
        && tier.maxInputTokens <= 2_147_483_647,
  );
}

export function tokenRatesForInput(pricing: Extract<AiPricing, { mode: "token" }>, inputTokens: number): AiTokenRates {
  return pricing.contextTiers?.find(tier => tier.maxInputTokens === null || inputTokens <= tier.maxInputTokens) ?? pricing.contextTiers?.at(-1) ?? pricing;
}

export type AiPricing =
  | {
      mode: "token";

      input: number | null;
      cacheWrite?: number | null;

      cached: number | null;
      output: number | null;
      contextTiers?: AiTokenTier[];
      webSearch?: number | null;
      multiplier?: number | null;
      media?: AiMediaPrice[];
      requestPrice?: number | null;
    }
  | {
      mode: "spec";
      unit: AiPriceUnit;
      axes: AiSpecAxis[];
      tiers: AiPriceTier[];
      /** 默认档。调用方没指定规格时按它计价并发给上游 */
      default: SpecEntry[];
      multiplier?: number | null;
      media?: AiMediaPrice[];
      webSearch?: number | null;
      tokenInput?: AiTokenInputPricing;
      requestPrice?: number | null;
      requestPricing?: { axes: AiSpecAxis[]; tiers: AiPriceTier[]; default: SpecEntry[] };
      outputEnabled?: boolean;
      outputTokenSource?: "total" | "image";
      outputs?: AiOutputPrice[];
    };

/** Spec output prices can be combined with input tokens without billing output tokens twice. */
export function tokenPricingForBilling(pricing: AiPricing): Extract<AiPricing, { mode: "token" }> | null {
  if (pricing.mode === "token") return pricing;
  if (!pricing.tokenInput) return null;
  return { mode: "token", ...pricing.tokenInput, output: 0,
    contextTiers: pricing.tokenInput.contextTiers?.map(tier => ({ ...tier, output: 0 })) };
}

export function hasInputTokenCharge(pricing: AiPricing): boolean {
  const tokens = tokenPricingForBilling(pricing);
  return !!tokens && [tokens, ...(tokens.contextTiers ?? [])].some(rates =>
    [rates.input, rates.cached, rates.cacheWrite].some(rate => (rate ?? 0) > 0),
  );
}

export function toTokenInputPricing(pricing: Extract<AiPricing, { mode: "token" }>): AiTokenInputPricing {
  const input = ({ input, cached, cacheWrite }: AiTokenRates) => ({ input, cached, ...(cacheWrite === undefined ? {} : { cacheWrite }) });
  return { ...input(pricing), ...(pricing.contextTiers ? { contextTiers: pricing.contextTiers.map(tier => ({ maxInputTokens: tier.maxInputTokens, ...input(tier) })) } : {}) };
}

/** Saving a blank cache-write rate explicitly means free, never the input rate. */
export function withCacheWriteDefaults<T extends AiPricing>(pricing: T): T {
  if (pricing.mode === "token") return {
    ...pricing,
    cacheWrite: pricing.cacheWrite ?? 0,
    ...(pricing.contextTiers ? { contextTiers: pricing.contextTiers.map(tier => ({ ...tier, cacheWrite: tier.cacheWrite ?? 0 })) } : {}),
  };
  if (!pricing.tokenInput) return pricing;
  return {
    ...pricing,
    tokenInput: {
      ...pricing.tokenInput,
      cacheWrite: pricing.tokenInput.cacheWrite ?? 0,
      ...(pricing.tokenInput.contextTiers ? { contextTiers: pricing.tokenInput.contextTiers.map(tier => ({ ...tier, cacheWrite: tier.cacheWrite ?? 0 })) } : {}),
    },
  };
}

export function tokenInputPricingError(pricing: AiPricing): string | null {
  if (pricing.mode !== "spec" || !pricing.tokenInput) return null;
  return [pricing.tokenInput, ...(pricing.tokenInput.contextTiers ?? [])].some(rate => rate.input === null)
    ? "请填写输入 Token 积分单价，免费请填 0" : null;
}

export const TOKEN_MULTIPLIER_MIN = 0.01;
export const TOKEN_MULTIPLIER_MAX = 1000;

function validTokenMultiplier(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= TOKEN_MULTIPLIER_MIN && value <= TOKEN_MULTIPLIER_MAX;
}

export function tokenPriceMultiplier(pricing: { multiplier?: unknown }, defaultMultiplier = 1): number {
  return validTokenMultiplier(pricing.multiplier) ? pricing.multiplier : validTokenMultiplier(defaultMultiplier) ? defaultMultiplier : 1;
}

export function publicModelPricing(pricing: AiPricing, defaultMultiplier = 1): AiPricing {
  const multiplier = tokenPriceMultiplier(pricing, defaultMultiplier);
  const price = (value: number | null) => value === null ? null : Number((value * multiplier).toPrecision(12));
  const media = pricing.media === undefined ? {} : { media: publicMediaPrices(pricing.media, price) };
  const request = pricing.requestPrice === undefined ? {} : { requestPrice: price(pricing.requestPrice) };
  if (pricing.mode === "spec") {
    const tokens = tokenPricingForBilling(pricing);
    return { mode: "spec", unit: pricing.unit, axes: pricing.axes, tiers: pricing.tiers.map(tier => ({ ...tier, price: price(tier.price) })), default: pricing.default,
      ...(pricing.outputs ? { outputs: pricing.outputs.map(output => ({ ...output, tiers: output.tiers.map(tier => ({ ...tier, price: price(tier.price) })) })) } : {}),
      ...(tokens ? { tokenInput: toTokenInputPricing(publicModelPricing({ ...tokens, multiplier }) as Extract<AiPricing, { mode: "token" }>) } : {}),
      ...(pricing.webSearch === undefined ? {} : { webSearch: price(pricing.webSearch) }), ...media, ...request,
      ...(pricing.outputEnabled === undefined ? {} : { outputEnabled: pricing.outputEnabled }),
      ...(pricing.outputTokenSource === undefined ? {} : { outputTokenSource: pricing.outputTokenSource }),
      ...(pricing.requestPricing ? { requestPricing: { ...pricing.requestPricing, tiers: pricing.requestPricing.tiers.map(tier => ({ ...tier, price: price(tier.price) })) } } : {}),
    };
  }
  const write = (rates: AiTokenRates) => rates.cacheWrite === undefined ? {} : { cacheWrite: price(rates.cacheWrite) };
  return { mode: "token", input: price(pricing.input), ...write(pricing), cached: price(pricing.cached), output: price(pricing.output),
    ...(pricing.contextTiers ? { contextTiers: pricing.contextTiers.map(tier => ({ maxInputTokens: tier.maxInputTokens, input: price(tier.input), ...write(tier), cached: price(tier.cached), output: price(tier.output) })) } : {}),
    ...(pricing.webSearch === undefined ? {} : { webSearch: price(pricing.webSearch) }),
    ...media, ...request,
  };
}

export const EMPTY_TOKEN_PRICING: AiPricing = {
  mode: "token",
  input: null,
  cached: null,
  output: null,
};

const UNITS = new Set<string>(["call", "image", "second", "million_tokens"]);

function toPrice(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;

  const num = Number(value);

  return Number.isFinite(num) && num >= 0 && num <= 2_147_483_647 ? Number(num.toFixed(6)) : null;
}

function toTokenPrice(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) && num >= 0 && num <= 2_147_483_647 ? Number(num.toFixed(6)) : null;
}

export function priceValue(price: number | null): number {
  return price ?? 0;
}

export function normalizePricing(value: unknown): AiPricing {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return EMPTY_TOKEN_PRICING;
  }

  const raw = value as Record<string, unknown>;
  const media = raw.media === undefined ? {} : { media: normalizeMediaPrices(raw.media) };

  if (raw.mode !== "spec") {
    const contextTiers = Array.isArray(raw.contextTiers) ? raw.contextTiers.map(item => {
      const tier = (item ?? {}) as Record<string, unknown>;
      return { maxInputTokens: tier.maxInputTokens === null ? null : Number(tier.maxInputTokens), input: toTokenPrice(tier.input), ...(tier.cacheWrite === undefined ? {} : { cacheWrite: toTokenPrice(tier.cacheWrite) }), cached: toTokenPrice(tier.cached), output: toTokenPrice(tier.output) };
    }) : undefined;
    return {
      mode: "token",
      input: toTokenPrice(raw.input),
      ...(raw.cacheWrite === undefined ? {} : { cacheWrite: toTokenPrice(raw.cacheWrite) }),
      cached: toTokenPrice(raw.cached),
      output: toTokenPrice(raw.output),
      ...(contextTiers && validTokenTiers(contextTiers) ? { contextTiers } : {}),
      ...(raw.webSearch === undefined ? {} : { webSearch: toTokenPrice(raw.webSearch) }),
      ...(raw.requestPrice === undefined ? {} : { requestPrice: toPrice(raw.requestPrice) }),
      ...(raw.multiplier === undefined ? {} : { multiplier: validTokenMultiplier(raw.multiplier) ? raw.multiplier : null }),
      ...media,
    };
  }

  const axes = Array.isArray(raw.axes)
    ? raw.axes
        .map((item) => {
          const entry = (item ?? {}) as Record<string, unknown>;
          const name = typeof entry.name === "string" ? entry.name.trim() : "";
          const role =
            typeof entry.role === "string" && ROLE_VALUES.has(entry.role)
              ? (entry.role as AiSpecRole)
              : "custom";
          const values = Array.isArray(entry.values)
            ? entry.values
                .map((v) => (typeof v === "string" ? v.trim() : ""))
                .filter((v) => v.length > 0)
            : [];

          return name && values.length > 0 ? { role, name, values } : null;
        })
        .filter((item): item is AiSpecAxis => item !== null)
    : [];

  const tiers = Array.isArray(raw.tiers)
    ? raw.tiers
        .map((item) => {
          const entry = (item ?? {}) as { specs?: unknown; price?: unknown };

          return {
            specs: normalizeSpecEntries(entry.specs),
            price: toPrice(entry.price),
            ...((entry as Record<string, unknown>).maxInputTokens === undefined ? {} : { maxInputTokens: (entry as Record<string, unknown>).maxInputTokens === null ? null : Number((entry as Record<string, unknown>).maxInputTokens) }),
          };
        })
    : [];

  return {
    mode: "spec",
    unit: UNITS.has(raw.unit as string) ? (raw.unit as AiPriceUnit) : "call",

    axes: axes.length > 0 ? axes : rebuildAxesFromTiers(tiers),
    tiers,
    default: normalizeSpecEntries(raw.default),
    ...(raw.tokenInput && typeof raw.tokenInput === "object" ? { tokenInput: toTokenInputPricing(normalizePricing({ ...raw.tokenInput, mode: "token", output: 0 }) as Extract<AiPricing, { mode: "token" }>) } : {}),
    ...(raw.webSearch === undefined ? {} : { webSearch: toTokenPrice(raw.webSearch) }),
    ...(raw.requestPrice === undefined ? {} : { requestPrice: toPrice(raw.requestPrice) }),
    ...(typeof raw.outputEnabled === "boolean" ? { outputEnabled: raw.outputEnabled } : {}),
    ...(raw.outputTokenSource === "image" || raw.outputTokenSource === "total" ? { outputTokenSource: raw.outputTokenSource } : {}),
    ...(raw.outputs === undefined ? {} : { outputs: normalizeOutputPrices(raw.outputs) }),
    ...(raw.requestPricing && typeof raw.requestPricing === "object" ? { requestPricing: (() => { const p = normalizePricing({ ...raw.requestPricing, mode: "spec", unit: "call" }) as Extract<AiPricing, { mode: "spec" }>; return { axes: p.axes, tiers: p.tiers, default: p.default }; })() } : {}),
    ...(raw.multiplier === undefined ? {} : { multiplier: validTokenMultiplier(raw.multiplier) ? raw.multiplier : null }),
    ...media,
  };
}

function guessRole(name: string): AiSpecRole {
  if (/^token$|token.*上限|上下文|context/i.test(name)) return "context";
  if (/质量|清晰|画质|quality/i.test(name)) return "quality";
  if (/尺寸|分辨率|大小|size|resolution/i.test(name)) return "size";
  if (/比例|aspect/i.test(name)) return "aspect";
  if (/时长|秒|duration/i.test(name)) return "duration";
  if (/声|音|audio/i.test(name)) return "audio";

  return "custom";
}

export function rebuildAxesFromTiers(tiers: AiPriceTier[]): AiSpecAxis[] {
  const order: string[] = [];
  const values = new Map<string, string[]>();

  for (const tier of tiers) {
    for (const { name, value } of tier.specs) {
      if (!values.has(name)) {
        order.push(name);
        values.set(name, []);
      }

      const list = values.get(name)!;

      if (!list.includes(value)) list.push(value);
    }
  }

  return order.map((name) => ({
    role: guessRole(name),
    name,
    values: values.get(name) ?? [],
  }));
}

export function toSpecAxes(axes: AiSpecAxis[]): SpecAxis[] {
  return axes.map((axis) => ({ name: axis.name, values: axis.values }));
}

export function specsByRole(
  axes: AiSpecAxis[],
  specs: SpecValues,
): Partial<Record<AiSpecRole, string>> {
  const out: Partial<Record<AiSpecRole, string>> = {};

  for (const axis of axes) {
    if (axis.role === "custom" || axis.role === "context") continue;

    const picked = specs[axis.name];

    if (picked) {
      out[axis.role] = picked;
    }
  }

  return out;
}

export function matchPriceTier(
  pricing: Extract<AiPricing, { mode: "spec" }>,
  specs: SpecValues,
): AiPriceTier | null {
  if (pricing.tiers.length === 0) return null;

  if (pricing.axes.length === 0) {
    return pricing.tiers[0] ?? null;
  }

  const wanted = normalizeSpecValues(specs);
  const exact = pricing.tiers.find((tier) => {
    const own = normalizeSpecValues(tier.specs);
    const names = Object.keys(own);

    return (
      names.length === Object.keys(wanted).length &&
      names.every((name) => own[name] === wanted[name])
    );
  });

  if (exact) return exact;

  return pricing.tiers.reduce((most, tier) =>
    priceValue(tier.price) > priceValue(most.price) ? tier : most,
  );
}

export function calcSpecCost(input: {
  pricing: Extract<AiPricing, { mode: "spec" }>;
  specs: SpecValues;
  quantity: number;
}): { points: number; tier: AiPriceTier | null; specText: string } {
  const tier = matchPriceTier(input.pricing, input.specs);
  const specText = buildSpecText(input.specs, toSpecAxes(input.pricing.axes));
  const quantity = Math.max(1, Math.round(input.quantity));

  return {

    points: Math.ceil(priceValue(tier?.price ?? null) * quantity),
    tier,
    specText,
  };
}

/** Context is an automatic pricing axis, independent of the user's generation parameters. */
export function matchOutputPriceTier(pricing: Extract<AiPricing, { mode: "spec" }>, specs: SpecValues, inputTokens: number): AiPriceTier | null {
  const context = pricing.axes.find(axis => axis.role === "context");
  if (!context) return !pricing.axes.length && pricing.unit === "million_tokens"
    ? pricing.tiers.find(tier => tier.maxInputTokens == null || inputTokens <= tier.maxInputTokens) ?? pricing.tiers.at(-1) ?? null
    : matchPriceTier(pricing, specs);
  const value = context.values.find(value => value === "不限" || inputTokens <= Number(value)) ?? context.values.at(-1)!;
  const defaults = defaultSpecValues(pricing);
  return matchPriceTier(pricing, Object.fromEntries(pricing.axes.map(axis => [axis.name, axis === context ? value : specs[axis.name] ?? defaults[axis.name] ?? axis.values[0]])));
}

export function defaultSpecValues(pricing: AiPricing): SpecValues {
  if (pricing.mode !== "spec") return {};

  const configured = normalizeSpecValues(pricing.default);

  if (Object.keys(configured).length > 0) return configured;

  return normalizeSpecValues(pricing.tiers[0]?.specs ?? []);
}

export const DEFAULT_IMAGE_AXES: AiSpecAxis[] = [
  { role: "quality", name: "质量", values: ["low", "medium", "high"] },
  { role: "size", name: "尺寸", values: ["1024x1024", "1536x1024", "1024x1536"] },
];

export function imageSpecPricing(): Extract<AiPricing, { mode: "spec" }> {
  return {
    mode: "spec", unit: "image", axes: DEFAULT_IMAGE_AXES,
    tiers: expandSpecCombinations(DEFAULT_IMAGE_AXES).map(specs => ({ specs: toSpecEntries(specs, DEFAULT_IMAGE_AXES), price: null })),
    default: [],
  };
}

const KINDS_PRICED_BY_SPEC = ["image_gen", "video_gen"];

export function defaultPricingForKind(kind: string): AiPricing {
  if (!KINDS_PRICED_BY_SPEC.includes(kind)) {
    return {
      mode: "token",
      input: null,
      cached: null,
      output: null,
    };
  }

  return {
    mode: "spec",
    unit: kind === "video_gen" ? "second" : "image",
    axes: [],
    tiers: [{ specs: [], price: null }],
    default: [],
  };
}

/** Operator draft defaults only; missing imported quotes and runtime rates stay missing. */
export function newModelPricingForKind(kind: string): AiPricing {
  const pricing = defaultPricingForKind(kind);
  return pricing.mode === "token"
    ? { ...pricing, input: 100, cacheWrite: 0, cached: 0, output: 0 }
    : { ...pricing, tokenInput: { input: 100, cacheWrite: 0, cached: 0 } };
}

export function priceUnitLabel(unit: AiPriceUnit): string {
  return unit === "image" ? "张" : unit === "second" ? "秒" : unit === "million_tokens" ? "M Token" : "次";
}

export function specPriceRange(
  pricing: AiPricing,
): { min: number; max: number } | null {
  if (pricing.mode !== "spec") return null;

  const prices = pricing.tiers
    .map((tier) => tier.price)
    .filter((price): price is number => price !== null);

  if (prices.length === 0) return null;

  return { min: Math.min(...prices), max: Math.max(...prices) };
}

export function pricingError(pricing: AiPricing, options: { hasOutputPrice?: boolean } = {}): string | null {
  if (pricing.mode === "spec" && pricing.outputs !== undefined) {
    const error = outputPricingError(pricing.outputs, pricing.outputEnabled === false || options.hasOutputPrice === false);
    if (error) return error;
    if (pricing.media?.some(rule => rule.target === "image_output")) return "图片输出请统一在输出计费中设置";
  }
  if ([pricing.webSearch, pricing.requestPrice].some(price => price === null)) return "请填写附加计费单价";
  const tokens = tokenPricingForBilling(pricing);
  const prices = [pricing.webSearch, pricing.requestPrice,
    ...(tokens ? [tokens, ...(tokens.contextTiers ?? [])].flatMap(rate => [rate.input, rate.cached, rate.cacheWrite, rate.output]) : []),
    ...(pricing.mode === "spec" ? [...pricing.tiers.map(tier => tier.price), ...(pricing.requestPricing?.tiers.map(tier => tier.price) ?? [])] : []),
  ];
  if (prices.some(price => price != null && (!Number.isFinite(price) || price < 0 || price > 2_147_483_647))) return "积分单价必须为非负有限数值且不超出范围";
  if (tokens && [tokens, ...(tokens.contextTiers ?? [])].some(rate => rate.input === null || (pricing.mode === "token" && options.hasOutputPrice !== false && rate.output === null))) return "请填写输入、输出 Token 单价";
  if (tokens?.contextTiers && !validTokenTiers(tokens.contextTiers)) return "输入上限须依次递增，且只有最后一行可留空表示不限";
  if (pricing.mode === "spec") {
    const contexts = pricing.axes.filter(axis => axis.role === "context");
    if (contexts.length > 1) return "token 只能设置一个规格类";
    if (contexts.length) {
      if (pricing.unit !== "million_tokens") return "token 规格仅用于 M Token 计费";
      const limits = contexts[0].values.map(value => ({ maxInputTokens: value === "不限" ? null : /^\d+$/.test(value) ? Number(value) : NaN, input: 0, cached: 0, output: 0 }));
      if (!validTokenTiers(limits)) return "token 规格值须为递增的正整数，只有末项可填不限";
      if (pricing.axes.reduce((count, axis) => count * axis.values.length, 1) > 8000) return "输出规格组合过多";
      const signature = (specs: SpecValues) => JSON.stringify(pricing.axes.map(axis => specs[axis.name]));
      const priced = new Set(pricing.tiers.map(tier => signature(normalizeSpecValues(tier.specs))));
      const missing = expandSpecCombinations(pricing.axes).some(specs => !priced.has(signature(specs)));
      if (missing) return "请补全每个输出规格的价格";
    }
    if (pricing.outputEnabled !== false && options.hasOutputPrice !== false) {
      if (!pricing.tiers.length || pricing.tiers.some(tier => tier.price === null)) return "请填写每个输出规格的积分单价";
      if (pricing.unit === "million_tokens" && !pricing.axes.length && !validTokenTiers(pricing.tiers.map(tier => ({ maxInputTokens: tier.maxInputTokens ?? null, input: 0, cached: 0, output: tier.price })))) return "输出上限须依次递增，且只有最后一行可留空表示不限";
    }
    if (pricing.requestPricing && (!pricing.requestPricing.tiers.length || pricing.requestPricing.tiers.some(tier => tier.price === null))) return "请填写请求计费单价";
  }
  return null;
}

/** Converts the editor to separate input/output prices; persisted prices are never updated on read. */
export function operationalPricing(pricing: AiPricing, kind: string): Extract<AiPricing, { mode: "spec" }> {
  let result: Extract<AiPricing, { mode: "spec" }> = pricing.mode === "spec" ? { ...pricing } : {
    mode: "spec", unit: "million_tokens", axes: [], default: [],
    tiers: pricing.contextTiers?.map(tier => ({ specs: [], price: tier.output, maxInputTokens: tier.maxInputTokens })) ?? [{ specs: [], price: pricing.output }],
    ...(["chat", "embedding", "rerank"].includes(kind) || pricing.input !== null || pricing.cached !== null || pricing.cacheWrite != null ? { tokenInput: toTokenInputPricing(pricing) } : {}),
    multiplier: pricing.multiplier, webSearch: pricing.webSearch, requestPrice: pricing.requestPrice,
  };
  if (result.unit === "call") {
    const request = { axes: result.axes, tiers: result.tiers, default: result.default };
    result = { ...result, unit: kind === "image_gen" ? "image" : "million_tokens", outputEnabled: true,
      tiers: result.tiers.map(tier => ({ ...tier, price: 0 })),
      ...(request.tiers.length === 1 && !request.axes.length ? { requestPrice: request.tiers[0].price } : { requestPricing: request }) };
  }
  const output = pricing.media?.find(item => item.target === "image_output");
  if (output) result = { ...result, outputEnabled: true, unit: output.unit === "million_tokens" ? "million_tokens" : "image", outputTokenSource: output.unit === "million_tokens" ? "image" : undefined,
    tiers: result.tiers.map(tier => ({ ...tier, price: output.unit === "image" || output.unit === "million_tokens" ? output.price : null })) };
  const image = pricing.media?.find(item => item.target === "image_input");
  result.media = [...(image ? [{ ...image, unit: image.unit === "million_tokens" ? "million_tokens" as const : "image" as const, price: image.unit === "image" || image.unit === "million_tokens" ? image.price : null, freeImages: 0, tokenMode: "replace" as const, tiers: [] }] : []),
    ...(pricing.media?.filter(item => item.target === "audio_input" || item.target === "video_input") ?? [])];
  if (kind === "embedding" || kind === "rerank") result.outputEnabled = false;
  else if (result.outputEnabled === false) result = { ...result, outputEnabled: true, tiers: result.tiers.map(tier => ({ ...tier, price: 0 })) };
  if (!result.tiers.length) result.tiers = [{ specs: [], price: null }];
  if (result.unit === "million_tokens" && !result.axes.some(axis => axis.role === "context") && result.tiers.some(tier => tier.maxInputTokens !== undefined)) {
    const name = "token";
    const values = result.tiers.map(tier => tier.maxInputTokens == null ? "不限" : String(tier.maxInputTokens));
    result = { ...result, axes: [...result.axes, { role: "context", name, values }],
      tiers: result.tiers.map(({ maxInputTokens: _maxInputTokens, ...tier }, index) => ({ ...tier, specs: [...tier.specs, { name, value: values[index] }] })),
      default: [...result.default, { name, value: values[0] }],
    };
  }
  if (output && pricing.mode === "token") result.outputs = outputPricesForEditor(pricing, kind);
  return withCacheWriteDefaults({ ...result, tokenInput: result.tokenInput ?? { input: 0, cacheWrite: 0, cached: null } });
}

export function newOutputPrice(target: AiOutputTarget): AiOutputPrice {
  return { target, unit: target === "image" ? "image" : target === "text" ? "million_tokens" : "second", axes: [], tiers: [{ specs: [], price: null }], default: [] };
}

export function outputPricesForEditor(pricing: AiPricing, kind: string): AiOutputPrice[] {
  if (pricing.mode === "spec" && pricing.outputs) return pricing.outputs;
  const target: AiOutputTarget = kind === "image_gen" ? "image" : kind === "video_gen" ? "video" : kind === "tts" ? "audio" : "text";
  const image = pricing.media?.find(rule => rule.target === "image_output");
  if (pricing.mode === "token") {
    const converted = operationalPricing({ ...pricing, media: undefined }, "chat");
    const text: AiOutputPrice = { target: "text", unit: "million_tokens", axes: converted.axes, default: converted.default, tiers: converted.tiers };
    return [text, ...(image ? [{ ...newOutputPrice("image"), unit: image.unit === "million_tokens" ? "million_tokens" as const : "image" as const, tiers: [{ specs: [], price: image.price }] }] : [])];
  }
  if (["embedding", "rerank"].includes(kind)) return [];
  const converted = operationalPricing(pricing, kind);
  const base = { target: converted.unit === "image" || converted.outputTokenSource === "image" ? "image" as const : converted.unit === "second" ? target : target === "image" ? "image" as const : "text" as const, unit: converted.unit, axes: converted.axes, tiers: converted.tiers, default: converted.default };
  return [base];
}

/** Keep the generation parameter projection on the model's primary output. */
export function withOutputPrices(pricing: Extract<AiPricing, { mode: "spec" }>, outputs: AiOutputPrice[], kind: string) {
  const target = kind === "image_gen" ? "image" : kind === "video_gen" ? "video" : kind === "tts" ? "audio" : "text";
  const primary = outputs.find(item => item.target === target) ?? outputs[0];
  return { ...pricing, ...(primary ? { unit: primary.unit, axes: primary.axes, tiers: primary.tiers, default: primary.default, outputTokenSource: primary.target === "image" ? "image" as const : "total" as const } : {}), outputs };
}

export function outputPricingError(outputs: readonly AiOutputPrice[], allowEmpty = false): string | null {
  if (!Array.isArray(outputs) || outputs.length > 4 || (!outputs.length && !allowEmpty)) return "请至少设置一项输出计费";
  if (new Set(outputs.map(item => item?.target)).size !== outputs.length) return "同一输出项目只能设置一次";
  for (const output of outputs) {
    if (!output || !AI_OUTPUT_TARGETS.some(item => item.value === output.target)) return "输出项目无效";
    if (!outputPriceUnits(output.target).some(item => item.value === output.unit)) return "输出计价单位无效";
    if (!Array.isArray(output.axes) || output.axes.length > 8 || !Array.isArray(output.tiers) || output.tiers.length > 8000 || !Array.isArray(output.default)) return "输出规格无效";
    const error = pricingError({ mode: "spec", ...output });
    if (error) return error;
  }
  return null;
}

function normalizeOutputPrices(raw: unknown): AiOutputPrice[] {
  if (!Array.isArray(raw) || raw.length > 4) throw new Error("输出计费配置无效");
  const outputs = raw.map(item => {
    if (!item || typeof item !== "object" || !AI_OUTPUT_TARGETS.some(target => target.value === item.target)) throw new Error("输出项目无效");
    const normalized = normalizePricing({ mode: "spec", unit: item.unit, axes: item.axes, tiers: item.tiers, default: item.default }) as Extract<AiPricing, { mode: "spec" }>;
    return { target: item.target as AiOutputTarget, unit: normalized.unit, axes: normalized.axes, tiers: normalized.tiers, default: normalized.default };
  });
  const error = outputPricingError(outputs, true);
  if (error) throw new Error(error);
  return outputs;
}

export type { SpecAxis, SpecEntry, SpecValues };
